import { Routes, Route, NavLink, Navigate } from 'react-router-dom';
import {
  BookOpen,
  Sparkles,
  FileText,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { LocationSelector } from '@/components/location/LocationSelector';
import { cn } from '@/lib/utils';

// Import sub-pages
import TemplateLibrary from './TemplateLibrary';
import RecipeDiscovery from './RecipeDiscovery';
import TemplateEditor from './TemplateEditor';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
  description?: string;
}

const navItems: NavItem[] = [
  {
    id: 'library',
    label: 'Template Library',
    icon: FileText,
    path: '/standardization/library',
    description: 'Browse and manage templates',
  },
  {
    id: 'discover',
    label: 'AI Discovery',
    icon: Sparkles,
    path: '/standardization/discover',
    description: 'Search and generate recipes with AI',
  },
];

export default function Standardization() {
  const header = (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <BookOpen className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            Standardization
          </h1>
          <p className="text-sm text-muted-foreground">
            Create and manage standardized recipes and product templates
          </p>
        </div>
      </div>
      <LocationSelector showAllOption={true} />
    </div>
  );

  return (
    <DashboardLayout header={header} fullWidth>
      <div className="flex gap-6 -mt-2 min-w-0 overflow-hidden">
        {/* Secondary Sidebar Navigation */}
        <aside className="w-56 shrink-0">
          <nav className="sticky top-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      'hover:bg-muted/70',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          <Routes>
            <Route path="library" element={<TemplateLibrary />} />
            <Route path="discover" element={<RecipeDiscovery />} />
            <Route path="new" element={<TemplateEditor />} />
            <Route path=":id" element={<TemplateEditor />} />
            <Route path="*" element={<Navigate to="/standardization/library" replace />} />
          </Routes>
        </div>
      </div>
    </DashboardLayout>
  );
}

